import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Stack,
  Tabs,
  Tab,
  IconButton,
  Tooltip
} from '@mui/material'
import {
  Add as AddIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Lock as LockIcon,
  Person as PersonIcon
} from '@mui/icons-material'
import { ticketService } from '../../../../shared/services/TicketService'
import { Ticket, TicketStatus } from '../../../../shared/types/entities'
import { useAuth } from '@shared/auth/AuthContext'
import { OrganizationFilter } from '../../components/filters/OrganizationFilter'
import { useNotification } from '../../hooks/useSignalR'
import { CreateTicketModal } from '../../components/ticket/CreateTicketModal'

function CustomerTickets() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [tickets, setTickets] = useState<Ticket[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<TicketStatus | 'All'>('All')
  const [organizationFilter, setOrganizationFilter] = useState('')
  const [childSiteFilter, setChildSiteFilter] = useState('')
  const [createModalOpen, setCreateModalOpen] = useState(false)

  const isOversightUser = user?.role === 'Oversight' || (user?.oversightChildSiteIds && user.oversightChildSiteIds.length > 0)

  const loadTickets = async () => {
    try {
      setLoading(true)
      setError(null)
      const filters = {
        status: statusFilter === 'All' ? undefined : statusFilter,
        search: searchQuery || undefined,
        organizationId: organizationFilter || undefined,
        childSiteId: childSiteFilter || undefined
      }
      // Use getAllTickets to respect organization visibility settings
      // Backend will automatically show org tickets if DefaultTicketVisibility = "Organization"
      const response = await ticketService.getAllTickets(1, 50, filters)
      setTickets(response.data)
    } catch (err: any) {
      setError(err.message || 'Failed to load tickets')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadTickets()
  }, [statusFilter, organizationFilter, childSiteFilter])

  // Real-time notifications - refresh ticket list when tickets are created, updated, or status changes
  useNotification('TicketCreated', () => {
    loadTickets()
  }, [statusFilter, organizationFilter, childSiteFilter])

  useNotification('TicketStatusChanged', () => {
    loadTickets()
  }, [statusFilter, organizationFilter, childSiteFilter])

  useNotification('TicketUpdated', () => {
    loadTickets()
  }, [statusFilter, organizationFilter, childSiteFilter])

  const handleSearch = () => {
    loadTickets()
  }

  const handleOrganizationChange = (orgId: string, siteId: string) => {
    setOrganizationFilter(orgId)
    setChildSiteFilter(siteId)
  }

  const getStatusColor = (status: TicketStatus): 'default' | 'warning' | 'info' | 'success' => {
    switch (status) {
      case 'Open':
        return 'warning'
      case 'InProgress':
        return 'info'
      case 'WaitingOnThirdParty':
        return 'warning'
      case 'Resolved':
        return 'success'
      case 'Closed':
        return 'default'
      default:
        return 'default'
    }
  }

  const getPriorityColor = (priority: string): string => {
    switch (priority) {
      case 'Urgent':
        return '#d32f2f'
      case 'High':
        return '#f57c00'
      case 'Medium':
        return '#fbc02d'
      case 'Low':
        return '#388e3c'
      default:
        return '#757575'
    }
  }

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">My Tickets</Typography>
        <Box display="flex" gap={1}>
          <Tooltip title="Refresh">
            <IconButton onClick={loadTickets} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateModalOpen(true)}
          >
            Create Ticket
          </Button>
        </Box>
      </Box>

      <Box mb={3}>
        <Tabs
          value={statusFilter}
          onChange={(_, value) => setStatusFilter(value)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="All" value="All" />
          <Tab label="Open" value="Open" />
          <Tab label="In Progress" value="InProgress" />
          <Tab label="Waiting on Third Party" value="WaitingOnThirdParty" />
          <Tab label="Resolved" value="Resolved" />
          <Tab label="Closed" value="Closed" />
        </Tabs>
      </Box>

      <Box mb={3}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            fullWidth
            placeholder="Search tickets by subject, description, or ticket number..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <Button size="small" onClick={handleSearch}>
                    Search
                  </Button>
                </InputAdornment>
              )
            }}
          />

          {isOversightUser && (
            <OrganizationFilter
              value={childSiteFilter || organizationFilter}
              onChange={handleOrganizationChange}
            />
          )}
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : tickets.length === 0 ? (
        <Card>
          <CardContent>
            <Box textAlign="center" py={4}>
              <Typography variant="h6" color="textSecondary" gutterBottom>
                No tickets found
              </Typography>
              <Typography variant="body2" color="textSecondary" mb={2}>
                {statusFilter === 'All'
                  ? "You haven't created any tickets yet."
                  : `No ${statusFilter.toLowerCase()} tickets found.`}
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateModalOpen(true)}
              >
                Create Your First Ticket
              </Button>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={2}>
          {tickets.map((ticket) => (
            <Card
              key={ticket.ticketId}
              sx={{
                cursor: ticket.isPrivate ? 'not-allowed' : 'pointer',
                '&:hover': { boxShadow: ticket.isPrivate ? 1 : 3 },
                borderLeft: `4px solid ${getPriorityColor(ticket.priority)}`,
                opacity: ticket.isPrivate ? 0.6 : 1,
                bgcolor: ticket.isPrivate ? 'action.hover' : 'background.paper'
              }}
              onClick={() => {
                if (ticket.isPrivate && isOversightUser) {
                  alert('This is a private ticket. You can see it exists but cannot access its details.')
                  return
                }
                navigate(`/customer/tickets/${ticket.ticketId}`)
              }}
            >
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box flex={1}>
                    <Box display="flex" alignItems="center" gap={1} mb={1} flexWrap="wrap">
                      <Typography variant="caption" color="textSecondary">
                        {ticket.ticketNumber}
                      </Typography>
                      <Chip
                        label={ticket.status}
                        color={getStatusColor(ticket.status)}
                        size="small"
                      />
                      <Chip
                        label={ticket.priority}
                        size="small"
                        sx={{
                          bgcolor: getPriorityColor(ticket.priority),
                          color: 'white'
                        }}
                      />
                      {ticket.categoryName && (
                        <Chip
                          label={ticket.categoryName}
                          size="small"
                          variant="outlined"
                          sx={{ borderColor: '#9c27b0', color: '#9c27b0' }}
                        />
                      )}
                      {ticket.isGroupTicket && (
                        <Chip
                          label="Group"
                          size="small"
                          color="secondary"
                          variant="filled"
                        />
                      )}
                      {isOversightUser && (ticket as any).childSiteName && (
                        <Chip
                          label={(ticket as any).childSiteName}
                          size="small"
                          color="secondary"
                          variant="outlined"
                        />
                      )}
                      {isOversightUser && !( ticket as any).childSiteName && (ticket as any).organizationName && (
                        <Chip
                          label={(ticket as any).organizationName}
                          size="small"
                          color="secondary"
                          variant="outlined"
                        />
                      )}
                      {ticket.isPrivate && (
                        <Chip
                          icon={<LockIcon fontSize="small" />}
                          label="Private"
                          size="small"
                          color="default"
                          variant="outlined"
                        />
                      )}
                      {isOversightUser && ticket.requesterName && (
                        <Chip
                          icon={<PersonIcon fontSize="small" />}
                          label={ticket.requesterName}
                          size="small"
                          color="info"
                          variant="outlined"
                        />
                      )}
                    </Box>
                    <Typography variant="h6" gutterBottom>
                      {ticket.subject}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical'
                      }}
                    >
                      {ticket.description}
                    </Typography>
                  </Box>
                  <Box ml={2} textAlign="right">
                    <Typography variant="caption" color="textSecondary" display="block">
                      Created
                    </Typography>
                    <Typography variant="body2">{formatDate(ticket.createdAt)}</Typography>
                    {ticket.dueDate && (
                      <>
                        <Typography variant="caption" color="textSecondary" display="block" mt={1}>
                          Due
                        </Typography>
                        <Typography
                          variant="body2"
                          color={
                            new Date(ticket.dueDate) < new Date() && ticket.status !== 'Resolved'
                              ? 'error'
                              : 'textPrimary'
                          }
                        >
                          {formatDate(ticket.dueDate)}
                        </Typography>
                      </>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {/* Create Ticket Modal */}
      <CreateTicketModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={(ticketId) => {
          // Refresh ticket list after creation
          loadTickets()
          // Optionally navigate to the new ticket
          navigate(`/customer/tickets/${ticketId}`)
        }}
        userType="customer"
      />
    </Box>
  )
}

export default CustomerTickets